import { useFrame, useLoader, useThree } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { useSocket } from "../../contexts/SocketContext";
import { useEffect, useRef } from "react";
import * as THREE from 'three';
import { Card } from "../../types/SharedTypes";
import { a, useSpring, easings } from "@react-spring/three";
import { createSafeInvalidate } from "../../tools/helpers";

type Coords = [number, number, number];

const DOWN: Coords = [0, 3.9, 4];
const UP: Coords = [0, 4.9, 3];
const UP_MOBILE: Coords = [0, 4.77, 3];

export const MainCard = ({canvasRef, currentCanvasCard}: {
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  currentCanvasCard: Card | null
}) => {

  const {nodes} = useLoader(GLTFLoader, '/3d/cardtest/cardtest.gltf');

  const {currentCard, game, currentPlayer, isCuttingDeck} = useSocket();
  const {invalidate} = useThree();
  const safeInvalidate = createSafeInvalidate(invalidate);

  const canvasTextureRef = useRef<THREE.CanvasTexture>(null);
  const objRef = useRef<THREE.Mesh>(null);
  const currentCardKeyRef = useRef('');
  const isAnimatingRef = useRef(false);

  const screenWidth = window.innerWidth;
  const upPosition = screenWidth > 500 ? UP : UP_MOBILE;

  const [spring, api] = useSpring({
    from: {
      position: upPosition,
    },
    config: {duration: 600, easing: easings.easeInOutSine},
    onStart: () => {
      isAnimatingRef.current = true;
      safeInvalidate();
    },
    onRest: () => {
      console.log('ANIM RESOLVE REAL');
      isAnimatingRef.current = false;
      safeInvalidate();
    },
  }, []);

  useEffect(() => {
    if (!game) return;

    if (game.currentState === 'round_waiting') {
      api.start({to: {position: DOWN}, config: { duration: 600}});
    }

  }, [game?.currentState]);

  useEffect(() => {
    if (!currentCard || !currentCanvasCard) return;

    const key1 = `${currentCard.rank}${currentCard.suit}`;
    const key2 = `${currentCanvasCard.rank}${currentCanvasCard.suit}`;

    if (canvasTextureRef.current && (key1 !== key2)) {
      setTimeout(() => {
        if (!canvasTextureRef.current) return;
        canvasTextureRef.current.needsUpdate = true;
        invalidate();
      }, 300);
    }

    invalidate();
    
  }, [currentCard, canvasRef.current, canvasTextureRef.current, currentCanvasCard]);

  useEffect(() => {
    if (isCuttingDeck) api.start({to: {position: DOWN}, config: { duration: 300}});
  }, [isCuttingDeck]);

  useEffect(() => {
    if (!currentCard) return;

    const newKey = `${game?.currentRound}${currentCard.rank}${currentCard.suit}`;
    if (currentCardKeyRef.current === newKey) return;

    currentCardKeyRef.current = newKey;
    
    if (spring.position.isAnimating) {
      spring.position.finish();
    }

    if (spring.position.get()[1] < 4) {
      api.start({to: {position: upPosition}, config: { duration: 600}});
      return;
    }

    //FIXME: issue with finishing anim since onRest calls regardless
    api.start({
      to: async (next) => {
        await next({position: DOWN, config: { duration: 300}});
        await next({position: upPosition, config: { duration: 100}});
      },
  });
  }, [currentCard]);

  //track vertical cam movement to card
  useFrame(({ camera }) => {

    if (isAnimatingRef.current || spring.position.isAnimating) invalidate();

    if (!objRef.current) return;

    // Get camera pitch in world space
    const cameraEuler = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
    const pitch = cameraEuler.x; // up/down tilt

    // Start with your card's default rotation
    const defaultEuler = new THREE.Euler(0, -Math.PI / 2, 0, 'YXZ');
    const defaultQuat = new THREE.Quaternion().setFromEuler(defaultEuler);

    // Create a quaternion that tilts forward/back along card's local X axis
    const tiltQuat = new THREE.Quaternion();
    tiltQuat.setFromAxisAngle(new THREE.Vector3(0, 0, -1), pitch);

    // Combine the rotations: default + tilt
    objRef.current.quaternion.copy(defaultQuat).multiply(tiltQuat);
  });

  if (!game || (currentPlayer && currentPlayer.lives < 1)) return null;
  return (
    <>
      <a.mesh
        ref={objRef}
        scale={[0.25, 0.25, -0.25]}
        position={spring.position}
        rotation={[0,-1.571,0]}
        //@ts-ignore
        geometry={nodes.Cube.geometry}
      >
        <meshBasicMaterial>
          <canvasTexture args={[canvasRef.current!]} ref={canvasTextureRef} attach="map" />
        </meshBasicMaterial>
      </a.mesh>
    </>
  )
}

export const PlayingCardCanvas = ({card: _card, canvasRef, setCurrentCanvasCard}: {
  card: Card | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  setCurrentCanvasCard: (prev: Card | null) => void
}) => {

  const {suitImages} = useSocket();

  useEffect(() => {

    console.log('CARD CHANGE', _card);

    const createImage = () => {
      //for override testing/fallback
      const card = _card || {suit: 'h', rank: 1}

      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const width = 800;
      const height = 600;

      const HERO_SUIT_SIZE = 240;

      canvas.width = width;
      canvas.height = height;

      const currentColour = card?.suit === 'h' || card?.suit === 'd' ? '#f31404' : 'black';

      // Clear the canvas
      ctx.clearRect(0, 0, width, height);

      // Draw card background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = 'lightslategray';
      ctx.fillRect(width/2, 0, width/2, height);

      ctx.strokeStyle = currentColour;
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.roundRect(15, 8, (width/2)-40, height-20, [52]);
      ctx.stroke();

      // Render card text (number and suit)
      ctx.fillStyle = currentColour;
      ctx.font = 'bold 54px Poppins';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      if (card) {
        const realRank = card.rank === 13 ? "K" : card.rank === 12 ? "Q" : card.rank === 11 ? "J" : card.rank === 1 ? "A" : card.rank.toString();
        const metrics = ctx.measureText(`${realRank}`);
        ctx.fillText(`${realRank}`, 40, 40);
        suitImages && ctx.drawImage(suitImages[card.suit], 40 + metrics.width + 8, 36, 50, 50);

        ctx.globalAlpha = 0.6;
        suitImages && ctx.drawImage(
          suitImages[card.suit],
          width/4.2-HERO_SUIT_SIZE/2,
          height/2-HERO_SUIT_SIZE/2,
          HERO_SUIT_SIZE,
          HERO_SUIT_SIZE
        );
        ctx.globalAlpha = 1;
        ctx.fillStyle = "white";
        ctx.font = 'bold 72px Poppins';
        ctx.textAlign = 'center';
        ctx.fillText(`${realRank}`, width/4.18, height/2-24);
      }
      else ctx.fillText('?', 40, 40);

      setCurrentCanvasCard(_card);
    }

    createImage();

  }, [_card]);

  return <canvas ref={canvasRef} style={{display: 'none'}} />;
};